/**
 * 針對非同步動態載入（Dynamic Import，如 Webpack 分塊 JS / CSS）遭遇網路瞬斷或 502/504 錯誤時的自動重試模組。
 */

/**
 * 判斷錯誤是否屬於靜態分塊資源載入失敗 (JS 或 CSS chunk)
 * @param {Error} error
 * @returns {boolean}
 */
export function isChunkLoadError(error) {
  if (!error) return false;
  const message = error.message || "";
  return (
    error.code === "CSS_CHUNK_LOAD_FAILED" ||
    error.name === "ChunkLoadError" ||
    /Loading (CSS )?chunk .* failed/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message)
  );
}

/**
 * 對動態 import() 函式進行自動重試封裝
 * @param {Function} importFn - 回傳 Promise 的動態載入函式，例如 () => import('@/views/...')
 * @param {number} [retries=3] - 最大重試次數，預設 3 次
 * @param {number} [interval=500] - 重試延遲時間 (ms)，預設 500ms
 * @param {number} [backoffRate=1.5] - 重試延遲遞增倍率，預設 1.5 倍
 * @returns {Promise<any>}
 */
export function retryImport(
  importFn,
  retries = 3,
  interval = 500,
  backoffRate = 1.5
) {
  return new Promise((resolve, reject) => {
    importFn()
      .then(resolve)
      .catch((error) => {
        if (!isChunkLoadError(error) || retries <= 0) {
          return reject(error);
        }

        console.warn(
          `[retryImport] Chunk 載入失敗: "${error.message}"，${interval}ms 後自動重試 (剩餘重試次數: ${retries})`
        );

        setTimeout(() => {
          retryImport(
            importFn,
            retries - 1,
            Math.round(interval * backoffRate),
            backoffRate
          )
            .then(resolve)
            .catch(reject);
        }, interval);
      });
  });
}

/**
 * 遍歷 Vue Router 路由設定陣列，為所有使用動態載入 (component 為函式) 的路由自動套用 retryImport
 * @param {Array<object>} routes - Vue Router 路由設定陣列
 * @param {number} [retries=3]
 * @param {number} [interval=500]
 * @returns {Array<object>} 原路由陣列 (in-place 修改並回傳)
 */
export function wrapRoutesWithRetry(routes, retries = 3, interval = 500) {
  if (!Array.isArray(routes)) return routes;

  routes.forEach((route) => {
    // 單一 component
    if (typeof route.component === "function") {
      const originalImport = route.component;
      // 避免重複包裝
      if (!originalImport.__hasRetryWrapper) {
        const wrapped = () => retryImport(originalImport, retries, interval);
        wrapped.__hasRetryWrapper = true;
        route.component = wrapped;
      }
    }

    // 命名視圖 components (多個 components)
    if (route.components && typeof route.components === "object") {
      Object.keys(route.components).forEach((viewName) => {
        const comp = route.components[viewName];
        if (typeof comp === "function" && !comp.__hasRetryWrapper) {
          const wrapped = () => retryImport(comp, retries, interval);
          wrapped.__hasRetryWrapper = true;
          route.components[viewName] = wrapped;
        }
      });
    }

    // 子路由遞迴處理
    if (Array.isArray(route.children)) {
      wrapRoutesWithRetry(route.children, retries, interval);
    }
  });

  return routes;
}
